import React from 'react';
import { convertEnumsToReadableStrings } from 'utils/helpers';
import { Button, Col, Form, Image, List, Row, Select, Typography, Upload, UploadContent } from 'components';
import { CloseIcon, CloseIconLight, DoneIcon } from 'components/common/icons';
import styles from '../styles.module.scss';
import { AttachmentItem } from '../hooks';

const { Title, Text } = Typography;
const { Dragger } = Upload;

interface AttachmentsSectionProps {
  attachments: AttachmentItem[];
  attachmentType: string | undefined;
  fileList: File[];
  error: string;
  disabledActions: boolean;
  attachmentTypes: any[];
  setAttachmentType: (type: string) => void;
  setFileList: (files: File[]) => void;
  handleAddAttachment: () => void;
  handleDeleteAttachment: (index: number) => void;
  handleFileSizeValidation: (file: File) => boolean | any;
  handleCancelAttachment: () => void;
}

export const AttachmentsSection: React.FC<AttachmentsSectionProps> = ({
  attachments,
  attachmentType,
  fileList,
  error,
  disabledActions,
  attachmentTypes,
  setAttachmentType,
  setFileList,
  handleAddAttachment,
  handleDeleteAttachment,
  handleFileSizeValidation,
  handleCancelAttachment,
}) => {
  const isImageFile = (file: File) => {
    return ['image/jpeg', 'image/png', 'image/jpg', 'image/gif'].includes(file.type);
  };

  const getFileIcon = (file: File) => {
    // Return appropriate icon based on file type
    if (file.name.endsWith('.pdf')) {
      return (
        <div className={styles.fileIcon} style={{ color: '#e74c3c' }}>
          PDF
        </div>
      );
    } else if (file.name.endsWith('.csv') || file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
      return (
        <div className={styles.fileIcon} style={{ color: '#27ae60' }}>
          XLS
        </div>
      );
    } else {
      return (
        <div className={styles.fileIcon} style={{ color: '#3498db' }}>
          FILE
        </div>
      );
    }
  };

  const getFilePreview = (file: File) => {
    if (isImageFile(file)) {
      // For images, show a thumbnail
      const imageUrl = URL.createObjectURL(file);
      return (
        <div className={styles.filePreview}>
          <Image src={imageUrl} width={48} height={48} style={{ objectFit: 'cover' }} />
        </div>
      );
    } else {
      // For non-images, show an icon
      return getFileIcon(file);
    }
  };

  return (
    <>
      <Title level={5} className={styles.sectionTitle}>
        Attachments
      </Title>

      {attachments.length > 0 && (
        <List
          dataSource={attachments}
          className={styles.attachmentsList}
          renderItem={(item, index) => (
            <List.Item
              className={styles.listItem}
              actions={[
                <Button
                  key={'add'}
                  size={'small'}
                  type={'link'}
                  icon={<CloseIconLight />}
                  onClick={() => handleDeleteAttachment(index)}
                />,
              ]}
            >
              <Row align="middle" style={{ width: '100%', paddingLeft: 8 }}>
                <Col flex="48px">{getFilePreview(item.file)}</Col>
                <Col flex="auto">
                  <Text ellipsis={{ tooltip: true }} style={{ height: 32, marginLeft: 12 }}>
                    {item.type && <span className={styles.optionTag}>{convertEnumsToReadableStrings(item.type)}</span>}{' '}
                    {item.file.name}
                  </Text>
                  <div style={{ fontSize: '12px', color: '#8c8c8c', marginLeft: 12 }}>
                    {item.file.size ? Math.abs(item.file.size / 1000) + ' KB' : 'Size N/A'}
                  </div>
                </Col>
              </Row>
            </List.Item>
          )}
        />
      )}

      <Row gutter={[16, 16]}>
        <Col span={12}>
          <Select
            size={'large'}
            value={attachmentType}
            options={attachmentTypes}
            onChange={setAttachmentType}
            style={{ width: '100%' }}
            placeholder="Select attachment type"
          />
        </Col>

        <Col span={12}>
          <Form.Item validateStatus={error ? 'error' : ''} help={error || ''}>
            <Dragger
              accept=".csv,.pdf,.png,.jpg,.jpeg"
              style={{ height: 40 }}
              beforeUpload={handleFileSizeValidation}
              fileList={fileList as any}
              onRemove={() => setFileList([])}
              maxCount={1}
            >
              <UploadContent
                title={'Upload or Drag and Drop File'}
                message={'Max size: 5MB / Format: CSV, PDF, PNG, JPG, JPEG'}
              />
            </Dragger>
          </Form.Item>
        </Col>
        <Col span={24}>
          <Row gutter={4} justify={'end'}>
            <Col>
              <Button
                disabled={disabledActions}
                color="default"
                variant="filled"
                onClick={handleCancelAttachment}
                icon={<CloseIcon />}
              />
            </Col>
            <Col>
              <Button disabled={disabledActions} type="primary" icon={<DoneIcon />} onClick={handleAddAttachment} />
            </Col>
          </Row>
        </Col>
      </Row>
    </>
  );
};
